export function createFluidVisuals({
  state,
  clamp,
  worldToBlock,
  blockGeo,
  lavaLevelGeos,
  waterLevelHeightByLevel,
  waterSourceLevel = 7,
  waterMinFlowLevel = 1,
  lavaSourceLevel = 4,
  lavaMinFlowLevel = 1,
  waterVisualLerpSpeed = 10,
  waterVisualUpdateBudgetPerFluidTick = 220
} = {}) {
  function normalizeWaterLevel(levelRaw) {
    const level = Math.floor(Number(levelRaw));
    if (Number.isFinite(level) && level >= waterMinFlowLevel && level <= waterSourceLevel + 1) return level;
    return waterSourceLevel;
  }

  function getWaterHeightForLevel(level = waterSourceLevel) {
    const normalized = normalizeWaterLevel(level);
    return waterLevelHeightByLevel.get(normalized) || 1.0;
  }

  function getLavaHeightForLevel(level = lavaSourceLevel) {
    const normalized = clamp(Math.floor(Number(level)), lavaMinFlowLevel, lavaSourceLevel);
    return lavaLevelGeos.heightByLevel.get(normalized) || 1.0;
  }

  function applyWaterHeightToMesh(mesh, height = 1) {
    if (!mesh || mesh.userData?.id !== 7) return;
    const h = clamp(Number(height) || 1, 0.08, 1);
    const baseY = Number(mesh.userData?.waterBaseY);
    const anchorY = Number.isFinite(baseY) ? baseY : worldToBlock(mesh.position.y);
    mesh.scale.set(1, h, 1);
    mesh.position.y = anchorY - ((1 - h) * 0.5);
    mesh.updateMatrix();
  }

  function getWaterGeometryForLevel(_level = waterSourceLevel) {
    return blockGeo;
  }

  function getLavaGeometryForLevel(level = lavaSourceLevel) {
    const normalized = clamp(Math.floor(Number(level)), lavaMinFlowLevel, lavaSourceLevel);
    return lavaLevelGeos.geometryByLevel.get(normalized) || blockGeo;
  }

  function applyWaterVisualHeight(mesh, level = waterSourceLevel, immediate = true) {
    if (!mesh || mesh.userData?.id !== 7) return;
    const targetHeight = getWaterHeightForLevel(level);
    mesh.userData.waterVisualTargetHeight = targetHeight;

    if (immediate || !Number.isFinite(mesh.userData.waterVisualHeight)) {
      mesh.userData.waterVisualHeight = targetHeight;
      applyWaterHeightToMesh(mesh, targetHeight);
      state.animatingWaterMeshes.delete(mesh);
      return;
    }

    state.animatingWaterMeshes.add(mesh);
  }

  function applyLavaVisualHeight(mesh, level = lavaSourceLevel) {
    if (!mesh || mesh.userData?.id !== 69) return;
    const geometry = getLavaGeometryForLevel(level);
    if (mesh.geometry !== geometry) mesh.geometry = geometry;
    mesh.userData.lavaVisualHeight = getLavaHeightForLevel(level);
    mesh.updateMatrix();
  }

  function applyLavaLightState(mesh, enabled) {
    if (!mesh || mesh.userData?.id !== 69) return;
    const existingLight = mesh.userData.lavaLight || null;
    if (existingLight) {
      mesh.remove(existingLight);
      mesh.userData.lavaLight = null;
    }
    mesh.userData.lavaGlowOnly = !!enabled;
  }

  function updateWaterVisualAnimations(delta = 0.016, opts = {}) {
    if (state.animatingWaterMeshes.size <= 0) {
      state.runtimeWaterVisualProcessed = 0;
      state.runtimeWaterVisualDeferred = 0;
      return;
    }
    const safeDelta = Math.max(0, Number(delta) || 0);
    const lerpT = 1 - Math.exp(-safeDelta * waterVisualLerpSpeed);
    const epsilon = 0.002;
    const maxUpdatesRaw = Math.floor(Number(opts?.maxUpdates));
    const maxUpdates = Number.isFinite(maxUpdatesRaw)
      ? Math.max(8, maxUpdatesRaw)
      : Math.max(8, state.runtimeWaterVisualBudget || waterVisualUpdateBudgetPerFluidTick);
    const isWithinFluidFocusAt = typeof opts?.isWithinFluidFocusAt === 'function'
      ? opts.isWithinFluidFocusAt
      : () => true;
    const focus = opts?.focus || null;
    let processed = 0;
    let deferred = 0;

    for (const mesh of state.animatingWaterMeshes) {
      if (!mesh || mesh.userData?.id !== 7 || !mesh.parent) {
        state.animatingWaterMeshes.delete(mesh);
        continue;
      }

      const target = Number(mesh.userData.waterVisualTargetHeight);
      if (!Number.isFinite(target)) {
        state.animatingWaterMeshes.delete(mesh);
        continue;
      }

      if (!isWithinFluidFocusAt(mesh.position.x, mesh.position.z, focus)) {
        mesh.userData.waterVisualHeight = target;
        applyWaterHeightToMesh(mesh, target);
        state.animatingWaterMeshes.delete(mesh);
        continue;
      }

      if (processed >= maxUpdates) {
        deferred += 1;
        continue;
      }

      processed += 1;
      const currentRaw = Number(mesh.userData.waterVisualHeight);
      const current = Number.isFinite(currentRaw) ? currentRaw : target;
      const next = current + ((target - current) * lerpT);

      if (Math.abs(next - target) <= epsilon) {
        mesh.userData.waterVisualHeight = target;
        applyWaterHeightToMesh(mesh, target);
        state.animatingWaterMeshes.delete(mesh);
        continue;
      }

      mesh.userData.waterVisualHeight = next;
      applyWaterHeightToMesh(mesh, next);
    }

    state.runtimeWaterVisualProcessed = processed;
    state.runtimeWaterVisualDeferred = deferred;
  }

  return {
    normalizeWaterLevel,
    getWaterHeightForLevel,
    getLavaHeightForLevel,
    getWaterGeometryForLevel,
    getLavaGeometryForLevel,
    applyWaterVisualHeight,
    applyLavaVisualHeight,
    applyLavaLightState,
    updateWaterVisualAnimations
  };
}
